import type { FC } from "react";
import { toast } from "react-toastify";
import { Copy01Icon, RefreshIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Badge,
  BadgeSize,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import type { OwnerReportShare } from "api/owner-report-shares";
import { shareMetaLine, STATE_LABELS, STATE_VARIANTS } from "./helpers";

/**
 * Every link this application has been shared on, newest first. A row is a share
 * rather than a recipient: one link, one state, one view count and one Revoke
 * across every address on it.
 */
export const SharedLinksTab: FC<{
  shares: OwnerReportShare[];
  failed: boolean;
  revokingId: number | null;
  onRevoke: (share: OwnerReportShare) => void;
  /** Takes the partner back to the Share tab with this link's recipients filled in. */
  onReshare: (share: OwnerReportShare) => void;
}> = ({ shares, failed, revokingId, onRevoke, onReshare }) => {
  const copy = (url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Could not copy. Select the link and copy it."));
  };

  // A failed fetch must not read as "never shared" — that would hide live links the partner
  // may have opened this tab specifically to revoke.
  if (failed)
    return (
      <p className="py-10 text-center text-sm text-content-500">
        These links could not be loaded. Please close this and try again.
      </p>
    );

  if (shares.length === 0)
    return (
      <p className="py-10 text-center text-sm text-content-400">
        This application has not been shared yet.
      </p>
    );

  return (
    // Scrolls within the dialog's body rather than growing it: the panel is a
    // fixed-height flex column now, and a long list of links would otherwise
    // push past max-h instead of scrolling.
    <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-6 py-2">
      {shares.map((share) => (
        <div
          key={share.id}
          className="flex flex-col gap-3 rounded-lg border border-alpha-40 px-4 py-3"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {share.emails.map((email) => (
                  <span
                    key={email}
                    className="truncate text-sm font-medium text-content-1000"
                  >
                    {email}
                  </span>
                ))}
              </div>
              <span className="text-xs text-content-400">
                {shareMetaLine(share)}
              </span>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <Badge variant={STATE_VARIANTS[share.state]} size={BadgeSize.SM}>
                {STATE_LABELS[share.state]}
              </Badge>

              <Show if={share.state === "active" && Boolean(share.url)}>
                <IconButton
                  HugeIcon={Copy01Icon}
                  variant={IconButtonVariant.TEXT_BASE}
                  size={IconButtonSize.SM}
                  onClick={() => copy(share.url ?? "")}
                  aria-label="Copy link"
                />
              </Show>

              <Show if={share.state === "active"}>
                <Button
                  variant={ButtonVariant.DANGER}
                  size={ButtonSize.SM}
                  onClick={() => onRevoke(share)}
                  loading={revokingId === share.id}
                  disabled={revokingId !== null}
                >
                  Revoke
                </Button>
              </Show>
            </div>
          </div>

          {/* The payload is frozen when a link is sent, so a recipient holding
              this one is still reading "In progress" for checks that have since
              finished. Nothing else would ever tell the partner that. */}
          <Show if={share.reports_finished_since_share}>
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-warning-50 px-3 py-2">
              <span className="flex items-center gap-2 text-xs text-content-700">
                <HugeiconsIcon icon={RefreshIcon} size={14} />
                Checks have finished since this was sent. Recipients still see
                them as in progress.
              </span>
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                onClick={() => onReshare(share)}
              >
                Share again
              </Button>
            </div>
          </Show>
        </div>
      ))}
    </div>
  );
};
